import {
  extractRawApexFromPayload,
  isApexPaygInviteFlag,
  isApexPaygMubPurchaseTimestamp
} from './apexFeatureInterpretation';

/**
 * Visibility rules for the Pay As You Go (Apex) promo banner on LMS / Netgear Partner subscription UI.
 */

/**
 * True when v2 `getPlanList.isCountrySupported` indicates Stripe monthly plans are not offered
 * (international / unsupported region). Handles boolean and occasional string/number shapes from APIs.
 * @param {unknown} value - Raw `isCountrySupported` from subscriptionPlans
 * @returns {boolean}
 */
export function isSubscriptionCountryStripeUnsupported(value) {
  if (value === false || value === 0 || value === '0') return true;
  if (typeof value === 'string' && value.trim().toLowerCase() === 'false') return true;
  return false;
}

export function hasLmsCreditSummaryObject(lmsResponse) {
  return (
    lmsResponse &&
    typeof lmsResponse === 'object' &&
    !Array.isArray(lmsResponse) &&
    Object.keys(lmsResponse).length > 0
  );
}

/**
 * True when credit summary `currentPlan` represents a Stripe term plan (monthly / multi-year), not LMS keys.
 * @param {Object|null|undefined} currentPlan - Transformed current plan from credit summary
 * @returns {boolean}
 */
export function isStripeTermSubscriptionPlan(currentPlan) {
  if (!currentPlan || typeof currentPlan !== 'object') return false;

  // MUB / PAYG Stripe usage billing is a billed subscription product, not LMS key pools — even if
  // credit summary ever sends a misleading subscriptionType (e.g. "lms"), still treat as Stripe term.
  if (isInsightMonthlyUsageBillingPlan(currentPlan)) return true;

  const sub = (currentPlan.subscriptionType || currentPlan.planType || '').toString().toLowerCase();
  if (sub === 'lms') return false;

  if (
    sub.includes('monthly') ||
    sub.includes('1-year') ||
    sub.includes('1 year') ||
    sub.includes('1yr') ||
    sub === '1year'
  ) {
    return true;
  }
  if (sub.includes('3-year') || sub.includes('3 year') || sub.includes('3yr') || sub === '3years') {
    return true;
  }
  if (sub.includes('5-year') || sub.includes('5 year') || sub.includes('5yr') || sub === '5years') {
    return true;
  }
  if (sub.includes('annual')) return true;

  const pname = (currentPlan.planName || currentPlan.planId || '').toString().toLowerCase();
  if (
    pname.includes('monthly') ||
    pname.includes('1-year') ||
    pname.includes('1 year') ||
    pname.includes('3-year') ||
    pname.includes('3 year') ||
    pname.includes('5-year') ||
    pname.includes('5 year')
  ) {
    return true;
  }

  const freq = currentPlan.rawData?.frequency;
  if (freq !== undefined && freq !== null && freq !== '') {
    if ([0, 1, 3, 4, '0', '1', '3', '4'].includes(freq)) return true;
  }

  return false;
}

/**
 * True when credit summary plan is Monthly Usage Billing (MUB / PAYG Stripe), e.g. planId
 * `Insight_Monthly_Usage_Billing_Standard_USD`. Used when `lmsResponse` is null after conversion.
 * @param {Object|null|undefined} currentPlan
 * @returns {boolean}
 */
export function isInsightMonthlyUsageBillingPlan(currentPlan) {
  if (!currentPlan || typeof currentPlan !== 'object') return false;
  const raw = currentPlan.rawData || {};
  const id = (currentPlan.planId || raw.planId || '').toString().toLowerCase();
  const name = (currentPlan.planName || raw.planName || '').toString().toLowerCase();
  if (id.includes('monthly_usage_billing')) return true;
  if (name.includes('monthly_usage_billing') || name.includes('usage billing')) return true;
  return false;
}

/**
 * True when credit summary plan is Pay As You Go (Apex PAYG), not legacy MUB (Apex `0`).
 * Requires Insight monthly usage billing (MUB) on `currentPlan`, plus Apex:
 * - UTC purchase timestamp (post–Stripe checkout), or
 * - invite flag `1` / `true` on credit summary / plan rawData, or
 * - `isApexEligibleFromFeature` from GET featureStatus/.../Apex when credit summary omits `Apex`.
 * @param {Object|null|undefined} currentPlan
 * @param {*} [creditSummaryApex] - Optional `details.Apex` from credit summary when not on `rawData`
 * @param {boolean} [isApexEligibleFromFeature] - True when feature API returns `details.Apex === 1`
 * @param {boolean} [isPaygMubFromApexFeature] - True when feature API returns Apex MUB purchase timestamp
 * @returns {boolean}
 */
export function isPayAsYouGoSubscriptionPlan(
  currentPlan,
  creditSummaryApex,
  isApexEligibleFromFeature = false,
  isPaygMubFromApexFeature = false
) {
  if (!isInsightMonthlyUsageBillingPlan(currentPlan)) return false;
  const raw = currentPlan?.rawData ?? currentPlan ?? {};
  const apex =
    creditSummaryApex ??
    extractRawApexFromPayload({ details: raw }) ??
    extractRawApexFromPayload(raw) ??
    raw.Apex ??
    raw.apex;
  if (isApexPaygMubPurchaseTimestamp(apex) || isApexPaygInviteFlag(apex)) return true;
  return Boolean(isApexEligibleFromFeature) || Boolean(isPaygMubFromApexFeature);
}

/**
 * @param {Object} p
 * @param {boolean} p.isApexEligible - GET user/v1/featureStatus/.../Apex → details.Apex === 1
 * @param {boolean} p.apexStatusLoading
 * @param {Object|null|undefined} p.currentPlan
 * @param {number|null|undefined} p.remainingTrialDays
 * @param {Object|null|undefined} p.lmsResponse
 * @param {boolean|string|number|undefined} [p.isCountrySupported] - From `getPlanList.isCountrySupported`; when unsupported, hide PAYG promo
 * @returns {boolean}
 */
export function shouldShowApexPayAsYouGoBanner({
  isApexEligible,
  apexStatusLoading,
  currentPlan,
  remainingTrialDays,
  lmsResponse,
  isCountrySupported
}) {
  // International: monthly Stripe / PAYG checkout is not available — do not show Apex PAYG invite banner
  if (isSubscriptionCountryStripeUnsupported(isCountrySupported)) return false;

  if (!isApexEligible || apexStatusLoading) return false;

  // Free trial: in progress or expiring today
  if (remainingTrialDays != null && remainingTrialDays > 0) return false;
  if (remainingTrialDays === 0) return false;

  // Free trial pending: no Stripe plan, no LMS credit object (matches Plan & Billing trial-pending case)
  const trialDaysUnset = remainingTrialDays === null || remainingTrialDays === undefined;
  if (trialDaysUnset && !currentPlan && !hasLmsCreditSummaryObject(lmsResponse)) {
    return false;
  }

  // Monthly / 1-yr / 3-yr / 5-yr / annual Stripe subscription
  if (isStripeTermSubscriptionPlan(currentPlan)) return false;

  return true;
}

/**
 * After Stripe MUB checkout on Apex PAYG, show Netgear Partner shell with device-day banner + PAYG plan card,
 * monthly-style org pools / purchase history, when **Apex API returns a purchase timestamp** (not `0` or `1`).
 *
 * - **Legacy MUB**: `details.Apex === 0` → `PurchasedSubscriptionList`
 * - **Apex + LMS invite**: `details.Apex === 1` → LMS Netgear flow + Activate PAYG banner (not this layout)
 * - **Apex + MUB purchased**: `details.Apex` = UTC epoch (ms) → this PAYG post-Stripe manage layout
 *
 * @param {Object} p
 * @param {boolean} p.isPaygMubFromApexTimestamp - True when Apex is a MUB purchase timestamp (merged from feature API, credit summary, or currentPlan.rawData in Plan & Billing)
 * @param {boolean} p.apexStatusLoading
 * @param {Object|null|undefined} p.currentPlan - Credit summary current plan (e.g. Monthly)
 * @param {number|null|undefined} p.remainingTrialDays
 * @param {boolean} p.hasLmsPlan - Same as Plan & Billing: post-trial LMS credit summary present
 * @param {boolean} p.isPlanCancelled
 * @returns {boolean}
 */
export function shouldShowApexPaygMonthlyManageView({
  isPaygMubFromApexTimestamp,
  apexStatusLoading,
  currentPlan,
  remainingTrialDays,
  hasLmsPlan,
  isPlanCancelled
}) {
  if (!currentPlan || isPlanCancelled) return false;

  const qualifiesMubPlan = isInsightMonthlyUsageBillingPlan(currentPlan);
  if (!hasLmsPlan && !qualifiesMubPlan) return false;

  if (remainingTrialDays != null && remainingTrialDays > 0) return false;
  if (remainingTrialDays === 0) return false;

  const label = (currentPlan.subscriptionType || currentPlan.planType || '').toString().toLowerCase();
  if (!label.includes('monthly') && !qualifiesMubPlan) return false;

  // Monthly PAYG shell requires a Stripe-term plan (includes MUB via planId) — not LMS-only credit rows.
  if (!isStripeTermSubscriptionPlan(currentPlan) && !qualifiesMubPlan) return false;

  // PAYG post-Stripe UI only when MUB was purchased on Apex PAYG (timestamp), not Apex 0 or LMS-only Apex 1.
  if (!isPaygMubFromApexTimestamp || apexStatusLoading) return false;

  return true;
}
